import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { courseDetails } from '@/mocks/courseDetailData';

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get('course') || 'ml-practical';
  const [selectedCourse, setSelectedCourse] = useState(preselected);
  const [submitted, setSubmitted] = useState(false);

  const course = courseDetails.find((c) => c.id === selectedCourse);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    const formData = new FormData(form);
    const url = 'https://readdy.ai/api/form/d918bt84jh3v1cgkssog';
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') {
        body.append(key, value.toString());
      }
    });

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
      .then(() => setSubmitted(true))
      .catch(() => setSubmitted(true));
  };

  if (submitted) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-28 pb-20 px-4 md:px-6 flex items-center justify-center bg-background-50">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-check-line text-3xl text-primary-500"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">报名提交成功！</h1>
            <p className="text-sm text-foreground-600 mb-2 leading-relaxed">
              感谢你选择bbgpt.online！
            </p>
            <p className="text-sm text-foreground-500 mb-8 leading-relaxed">
              我们的课程顾问将在<strong>24小时内</strong>通过邮件与你联系，确认课程安排和支付方式。请注意查收邮箱。
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xs"></i>
              </div>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-24 pb-20 px-4 md:px-6 bg-background-50">
        <div className="mx-auto max-w-6xl">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-3 md:gap-6 mb-12">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-900 text-background-50 text-xs font-semibold">1</div>
              <span className="text-xs text-foreground-900 font-medium hidden sm:block">选择课程</span>
            </div>
            <div className="w-8 md:w-12 h-px bg-foreground-300"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-900 text-background-50 text-xs font-semibold">2</div>
              <span className="text-xs text-foreground-900 font-medium hidden sm:block">填写信息</span>
            </div>
            <div className="w-8 md:w-12 h-px bg-background-200"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-xs font-semibold border border-background-200">3</div>
              <span className="text-xs text-foreground-400 font-medium hidden sm:block">确认提交</span>
            </div>
          </div>

          {/* Page Title */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">完成报名</h1>
            <p className="text-sm text-foreground-500">填写以下信息，开启你的AI学习之旅</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Form - Left Side */}
            <div className="lg:col-span-3">
              <form data-readdy-form="" onSubmit={handleSubmit}>
                {/* Personal Info */}
                <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-user-line text-primary-600 text-sm"></i>
                    </div>
                    <h2 className="font-heading text-lg text-foreground-950">个人信息</h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-xs text-foreground-600 mb-1.5 font-medium">姓名 <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder="请输入真实姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs text-foreground-600 mb-1.5 font-medium">邮箱 <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="phone" className="block text-xs text-foreground-600 mb-1.5 font-medium">手机号 <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder="请输入手机号"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-xs text-foreground-600 mb-1.5 font-medium">公司 / 学校</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder="选填"
                      />
                    </div>
                  </div>
                </div>

                {/* Course Selection */}
                <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-book-open-line text-primary-600 text-sm"></i>
                    </div>
                    <h2 className="font-heading text-lg text-foreground-950">选择课程</h2>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="course" className="block text-xs text-foreground-600 mb-1.5 font-medium">课程方案 <span className="text-red-500">*</span></label>
                    <select
                      id="course"
                      name="course"
                      value={selectedCourse}
                      onChange={(e) => setSelectedCourse(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all cursor-pointer appearance-none"
                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23666' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: '2.5rem' }}
                    >
                      {courseDetails.map((c) => (
                        <option key={c.id} value={c.id}>{c.title} — ¥{c.price.toLocaleString()}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="message" className="block text-xs text-foreground-600 mb-1.5 font-medium">留言备注</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                      placeholder="如有特殊需求或疑问，请在此留言（选填）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
                  </div>
                </div>

                {/* Anti-spam honeypot */}
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute opacity-0 pointer-events-none" />

                {/* Agreement & Submit */}
                <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                  {/* Non-refundable Notice */}
                  <div className="bg-foreground-900 rounded-xl p-5 mb-6">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-accent-500 text-base"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-background-50 mb-1">下单须知</h4>
                        <p className="text-xs text-background-300 leading-relaxed">
                          课程属于数字化知识服务产品，<strong className="text-background-200">购买后默认不予退款</strong>。仅在长期住院、重大疾病等特殊情况且提供有效证明时，方可申请退款审核。请在购买前仔细确认课程详情。
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 mb-6">
                    <input type="checkbox" id="agree" required className="mt-1 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                    <label htmlFor="agree" className="text-xs text-foreground-600 leading-relaxed cursor-pointer">
                      我已阅读并理解上述下单须知，充分了解课程内容后确认购买，并同意
                      <Link to="/terms" className="text-primary-500 hover:text-primary-600 mx-1 cursor-pointer font-medium">服务条款</Link>
                      和
                      <Link to="/refund-policy" className="text-primary-500 hover:text-primary-600 ml-1 cursor-pointer font-medium">退款政策</Link>
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3.5 bg-foreground-900 text-background-50 font-medium rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2"
                  >
                    确认并提交报名
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </button>

                  <p className="text-center text-xs text-foreground-400 mt-4">
                    提交后将由课程顾问与你联系确认课程安排
                  </p>
                </div>
              </form>
            </div>

            {/* Order Summary - Right Side */}
            <div className="lg:col-span-2">
              <div className="sticky top-24 space-y-6">
                {/* Course Card */}
                {course && (
                  <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                    <h2 className="font-heading text-lg text-foreground-950 mb-5">订单摘要</h2>

                    <div className="flex gap-4 mb-6 pb-6 border-b border-background-200">
                      <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={course.image} alt={course.title} className="w-full h-full object-cover object-top" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">{course.title}</h3>
                        <p className="text-xs text-foreground-400 mb-1">{course.duration} · {course.totalHours}课时</p>
                        <span className="inline-block text-xs px-2 py-0.5 bg-background-200 text-foreground-600 rounded-full whitespace-nowrap">{course.level}</span>
                      </div>
                    </div>

                    {/* Price Breakdown */}
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-500">课程原价</span>
                        <span className="text-foreground-600">¥{course.originalPrice.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-500">限时优惠</span>
                        <span className="text-accent-600 font-medium">-¥{(course.originalPrice - course.price).toLocaleString()}</span>
                      </div>
                      <div className="border-t border-background-200 pt-3 flex justify-between">
                        <span className="text-sm font-semibold text-foreground-900">应付金额</span>
                        <div className="text-right">
                          <span className="font-heading text-2xl text-primary-500">¥{course.price.toLocaleString()}</span>
                          <p className="text-xs text-foreground-400 mt-0.5">含税价格</p>
                        </div>
                      </div>
                    </div>

                    {/* What's Included */}
                    <div className="bg-background-50 rounded-xl p-4 mb-5">
                      <h4 className="text-xs font-semibold text-foreground-900 mb-3">课程包含</h4>
                      <ul className="space-y-2">
                        {course.features.slice(0, 6).map((f) => (
                          <li key={f} className="flex items-center gap-2 text-xs text-foreground-600">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className="ri-check-line text-accent-500 text-xs"></i>
                            </div>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Security Badges */}
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-lock-line text-sm"></i>
                        </div>
                        个人信息采用SSL加密传输
                      </div>
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-mail-check-line text-sm"></i>
                        </div>
                        24小时内课程顾问联系确认
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Methods */}
                <div className="bg-background-100 rounded-2xl p-6">
                  <h3 className="text-xs font-semibold text-foreground-900 mb-4">支持支付方式</h3>
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-background-50 rounded-lg border border-background-200 text-xs text-foreground-600">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-wechat-pay-line text-accent-500 text-sm"></i>
                      </div>
                      微信支付
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-background-50 rounded-lg border border-background-200 text-xs text-foreground-600">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-bank-card-line text-primary-500 text-sm"></i>
                      </div>
                      支付宝
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-background-50 rounded-lg border border-background-200 text-xs text-foreground-600">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-building-2-line text-foreground-400 text-sm"></i>
                      </div>
                      银行转账
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}